import {useContext, useEffect, useRef, useState} from "react";
import {useApi} from "../api";
import AppContext from "../context/AppContext";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencil, faTrash, faCheck, faTimes} from "@fortawesome/free-solid-svg-icons";
import './TagManagement.scss'

type TagEntry = { tag: string, count: number }
type SortMode = 'count' | 'alpha'

export default () => {
    const api = useApi()
    const { setTags: setGlobalTags } = useContext(AppContext)

    const [tags, setTags] = useState<TagEntry[]>([])
    const [filter, setFilter] = useState('')
    const [sort, setSort] = useState<SortMode>('count')
    const [renaming, setRenaming] = useState<string | null>(null)
    const [renameValue, setRenameValue] = useState('')
    const [deleting, setDeleting] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const renameInputRef = useRef<HTMLInputElement>(null)

    const load = async () => {
        const data = await api.getTagsWithCount().catch(() => [])
        setTags(data)
    }

    useEffect(() => { load() }, [])

    useEffect(() => {
        if (renaming !== null) renameInputRef.current?.focus()
    }, [renaming])

    const refreshGlobal = async () => {
        const all = await api.getAllTags().catch(() => [] as string[])
        setGlobalTags(all)
    }

    const startRename = (tag: string) => {
        setDeleting(null)
        setRenaming(tag)
        setRenameValue(tag)
    }

    const submitRename = async () => {
        const trimmed = renameValue.trim()
        if (!renaming || !trimmed) { setRenaming(null); return }
        if (trimmed === renaming) { setRenaming(null); return }
        setBusy(true)
        await api.renameTag(renaming, trimmed).catch(() => {})
        setRenaming(null)
        await load()
        await refreshGlobal()
        setBusy(false)
    }

    const confirmDelete = async (tag: string) => {
        setBusy(true)
        await api.deleteTag(tag).catch(() => {})
        setDeleting(null)
        await load()
        await refreshGlobal()
        setBusy(false)
    }

    const sorted = [...tags]
        .filter(t => t.tag.toLowerCase().includes(filter.toLowerCase()))
        .sort((a, b) => sort === 'alpha'
            ? a.tag.localeCompare(b.tag)
            : b.count - a.count)

    return (
        <div className="tag-mgmt">
            <div className="tm-toolbar">
                <input
                    className="tm-search"
                    type="text"
                    placeholder="Filter tags…"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
                <div className="tm-sort">
                    <button className={sort === 'count' ? 'active' : ''} onClick={() => setSort('count')}>By count</button>
                    <button className={sort === 'alpha' ? 'active' : ''} onClick={() => setSort('alpha')}>A–Z</button>
                </div>
                <span className="tm-total">{tags.length} tags</span>
            </div>

            <div className="tm-list">
                {sorted.map(({ tag, count }) => (
                    <div key={tag} className={'tm-row' + (busy ? ' tm-row--busy' : '')}>
                        {renaming === tag ? (
                            <div className="tm-inline-edit">
                                <input
                                    ref={renameInputRef}
                                    className="tm-rename-input"
                                    value={renameValue}
                                    onChange={e => setRenameValue(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter')  submitRename()
                                        if (e.key === 'Escape') setRenaming(null)
                                    }}
                                    disabled={busy}
                                />
                                {renameValue !== tag && tags.some(t => t.tag === renameValue.trim()) && (
                                    <span className="tm-merge-hint">will merge into existing tag</span>
                                )}
                                <button className="tm-action tm-save" onClick={submitRename} disabled={busy}>
                                    <FontAwesomeIcon icon={faCheck}/>
                                </button>
                                <button className="tm-action" onClick={() => setRenaming(null)} disabled={busy}>
                                    <FontAwesomeIcon icon={faTimes}/>
                                </button>
                            </div>
                        ) : deleting === tag ? (
                            <div className="tm-inline-confirm">
                                <span className="tm-confirm-text">Remove <strong>{tag}</strong> from {count} file{count !== 1 ? 's' : ''}?</span>
                                <button className="tm-action tm-danger" onClick={() => confirmDelete(tag)} disabled={busy}>
                                    Delete
                                </button>
                                <button className="tm-action" onClick={() => setDeleting(null)} disabled={busy}>
                                    <FontAwesomeIcon icon={faTimes}/>
                                </button>
                            </div>
                        ) : (
                            <>
                                <span className="tm-tag-name">{tag}</span>
                                <span className="tm-count">{count}</span>
                                <button className="tm-action" onClick={() => startRename(tag)} title="Rename / merge">
                                    <FontAwesomeIcon icon={faPencil}/>
                                </button>
                                <button className="tm-action tm-danger" onClick={() => { setRenaming(null); setDeleting(tag) }} title="Delete from all files">
                                    <FontAwesomeIcon icon={faTrash}/>
                                </button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
